// pages/public/UnitDetailPage.tsx
/**
 * @mockup /design/mockups/02-mobile-unit-detail.png
 * @description Unit detail page with photo gallery, floor plan, and features
 */
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ChevronLeft,
  Share,
  Check,
  Phone,
  Mail,
  ChevronRight,
  Heart,
  Loader2,
} from 'lucide-react';
import { PublicLayout } from '../../components/layout';
import { Lightbox, type Photo } from '../../components/unit';
import { StickyFooter, SavedUnitsDrawer, BeauxBot, QuickApplicationModal } from '../../components/public';
import { FloorPlanSVG, FloorPlanLegend } from '../../components/floorplans';
import { useSavedUnits } from '../../context/SavedUnitsContext';
import { useToast } from '../../components/ui/Toast';
import { formatPrice, formatAvailability, cn } from '../../lib/utils';
import { getUnit, trackEvent, type Unit, type Building } from '../../lib/api';

export function UnitDetailPage() {
  const { slug, unitId } = useParams<{ slug: string; unitId: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();

  // Data state
  const [unit, setUnit] = useState<Unit | null>(null);
  const [building, setBuilding] = useState<Building | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // UI state
  const [galleryIndex, setGalleryIndex] = useState<number | null>(null);
  const [currentHeroIndex, setCurrentHeroIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const [isSavedDrawerOpen, setIsSavedDrawerOpen] = useState(false);
  const [isApplicationOpen, setIsApplicationOpen] = useState(false);

  // Saved units context
  const { isSaved, toggleSave, count } = useSavedUnits();

  // Fetch unit data from API
  useEffect(() => {
    async function fetchUnit() {
      if (!unitId) return;

      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await getUnit(unitId);

      if (fetchError) {
        setError(fetchError);
        setUnit(null);
        setBuilding(null);
      } else if (data) {
        setUnit(data.unit);
        setBuilding(data.building);
      }

      setLoading(false);
    }

    fetchUnit();
  }, [unitId]);

  // Get photos from unit images or template images
  const photos: Photo[] = unit?.images?.map((img: any, i: number) => ({
    url: img.image_url,
    label: img.image_type === 'floorplan' ? 'Floor Plan' :
           img.image_type === 'living_room' ? 'Living Room' :
           img.image_type === 'kitchen' ? 'Kitchen' :
           img.image_type === 'bedroom' ? 'Bedroom' :
           img.image_type === 'bathroom' ? 'Bathroom' :
           `Photo ${i + 1}`,
  })) || [];

  const saved = unit ? isSaved(unit.id) : false;

  // Track unit view
  useEffect(() => {
    if (building?.id && unit?.id) {
      trackEvent('unit_view', building.id, unit.id);
    }
  }, [building?.id, unit?.id]);

  // Touch handlers for carousel
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe && currentHeroIndex < photos.length - 1) {
      setCurrentHeroIndex((prev) => prev + 1);
    }
    if (isRightSwipe && currentHeroIndex > 0) {
      setCurrentHeroIndex((prev) => prev - 1);
    }
    setTouchStart(0);
    setTouchEnd(0);
  };

  const nextSlide = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (currentHeroIndex < photos.length - 1) {
      setCurrentHeroIndex((prev) => prev + 1);
    }
  };

  const prevSlide = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (currentHeroIndex > 0) {
      setCurrentHeroIndex((prev) => prev - 1);
    }
  };

  const handleSave = () => {
    if (!unit) return;
    const wasAdded = toggleSave(unit as any);
    if (wasAdded) {
      if (count === 0) {
        showToast('Saved! View saved units below ↓', 'success');
      } else {
        showToast('Unit saved!', 'success');
      }
    } else {
      showToast('Removed from saved', 'info');
    }
  };

  const handleEmailClick = () => {
    if (!building) return;
    trackEvent('email_click', building.id, unit?.id);
    window.location.href = `mailto:${building.leasing_email}?subject=Inquiry about Unit ${unit?.unit_number} at ${building.name}`;
  };

  const handlePhoneClick = () => {
    if (!building || !building.leasing_phone) return;
    trackEvent('phone_click', building.id, unit?.id);
    window.location.href = `tel:${building.leasing_phone}`;
  };

  if (loading) {
    return (
      <PublicLayout showFooterPadding={false}>
        <div className="flex items-center justify-center min-h-screen bg-bg-base">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
          <span className="ml-3 text-text-secondary font-body">Loading unit...</span>
        </div>
      </PublicLayout>
    );
  }

  if (error || !unit || !building) {
    return (
      <PublicLayout showFooterPadding={false}>
        <div className="flex flex-col items-center justify-center min-h-screen p-8 text-center">
          <h1 className="text-2xl font-semibold text-gray-900 mb-2">
            Unit Not Found
          </h1>
          <p className="text-gray-600 mb-4">
            This unit is no longer available.
          </p>
          <button
            onClick={() => navigate(`/b/${slug}`)}
            className="text-primary font-medium"
          >
            ← Back to building
          </button>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout showFooterPadding>
      {/* Gallery Modal */}
      {galleryIndex !== null && (
        <Lightbox
          photos={photos}
          initialIndex={galleryIndex}
          onClose={() => setGalleryIndex(null)}
        />
      )}

      {/* Top Nav */}
      <div className="sticky top-0 bg-white/95 backdrop-blur-sm z-50 px-4 h-14 flex items-center justify-between border-b border-gray-100">
        <button
          onClick={() => navigate(`/b/${slug}`)}
          className="flex items-center text-primary font-medium active:opacity-70 transition-opacity"
        >
          <ChevronLeft size={24} className="-ml-2" />
          <span className="truncate max-w-[200px]">{building.name}</span>
        </button>
        <div className="flex items-center gap-1">
          <button
            onClick={handleSave}
            className={cn(
              'p-2 rounded-full transition-colors',
              saved
                ? 'bg-red-50 text-red-500'
                : 'text-gray-400 hover:bg-gray-100'
            )}
            aria-label={saved ? 'Remove from saved' : 'Save unit'}
          >
            <Heart size={20} className={saved ? 'fill-current' : ''} />
          </button>
          <button className="text-gray-400 p-2 hover:bg-gray-100 rounded-full transition-colors">
            <Share size={20} />
          </button>
        </div>
      </div>

      {/* Hero Image Carousel */}
      <div
        className="relative w-full aspect-[4/3] bg-gray-200 overflow-hidden group"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div
          className="flex transition-transform duration-300 ease-out h-full"
          style={{ transform: `translateX(-${currentHeroIndex * 100}%)` }}
        >
          {photos.map((photo, i) => (
            <img
              key={i}
              src={photo.url}
              alt={photo.label}
              className="w-full h-full object-cover flex-shrink-0"
            />
          ))}
        </div>

        {/* Navigation Arrows */}
        {currentHeroIndex > 0 && (
          <button
            onClick={prevSlide}
            className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 p-2 rounded-full shadow-md text-gray-800 hover:bg-white transition-colors"
          >
            <ChevronLeft size={20} />
          </button>
        )}
        {currentHeroIndex < photos.length - 1 && (
          <button
            onClick={nextSlide}
            className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 p-2 rounded-full shadow-md text-gray-800 hover:bg-white transition-colors"
          >
            <ChevronRight size={20} />
          </button>
        )}

        {/* Carousel Dots */}
        <div className="absolute bottom-4 left-0 right-0 flex justify-center space-x-2">
          {photos.map((_, i) => (
            <div
              key={i}
              className={`w-2 h-2 rounded-full shadow-sm backdrop-blur-sm transition-colors duration-200 ${
                i === currentHeroIndex ? 'bg-white' : 'bg-white/50'
              }`}
            />
          ))}
        </div>
      </div>

      <div className="p-4 pb-24 space-y-6">
        {/* Header */}
        <div className="space-y-2">
          <div className="flex justify-between items-start">
            <h1 className="text-2xl font-semibold text-gray-900">
              {unit.unit_type?.name || 'Unit'}
            </h1>
            <p className="text-xl font-bold text-primary">
              {formatPrice(unit.price)}/mo
            </p>
          </div>
          <div className="text-sm text-gray-500 space-y-1">
            <p>
              Floor {unit.floor} • Unit {unit.unit_number}
              {unit.square_feet && ` • ${unit.square_feet} sq ft`}
            </p>
            <p className={formatAvailability(unit.availability_date) === 'Available Now' ? 'text-green-600 font-medium' : ''}>
              {formatAvailability(unit.availability_date)}
            </p>
          </div>
        </div>

        {/* Save to Compare Button - Chunky for thumb-friendly tapping */}
        <button
          onClick={handleSave}
          className={cn(
            'w-full py-4 px-6 rounded-xl font-medium flex items-center justify-center gap-2 transition-all duration-200 min-h-[56px]',
            saved
              ? 'bg-red-50 text-red-600 border-2 border-red-200'
              : 'bg-gray-100 text-gray-700 border-2 border-gray-200 hover:border-gray-300 active:bg-gray-200'
          )}
        >
          <Heart size={24} className={saved ? 'fill-current' : ''} />
          <span className="text-base">{saved ? 'Saved to Compare ✓' : 'Save to Compare'}</span>
        </button>

        {/* Photo Grid */}
        <section>
          <h2 className="text-lg font-medium text-gray-900 mb-4">Photos</h2>
          <div className="grid grid-cols-3 gap-2">
            {photos.map((photo, i) => (
              <button
                key={i}
                onClick={() => setGalleryIndex(i)}
                className="relative aspect-square bg-gray-100 rounded-lg overflow-hidden border border-gray-100 active:opacity-90 transition-opacity w-full"
              >
                <img
                  src={photo.url}
                  alt={photo.label}
                  className="w-full h-full object-cover"
                />
                <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/60 to-transparent pt-4 pb-1 px-1">
                  <p className="text-[10px] text-white text-center font-medium leading-tight">
                    {photo.label}
                  </p>
                </div>
              </button>
            ))}
          </div>
        </section>

        {/* Features */}
        {unit.features && unit.features.length > 0 && (
          <section>
            <h2 className="text-lg font-medium text-gray-900 mb-4">Features</h2>
            <div className="space-y-3">
              {unit.features.map((item, i) => (
                <div key={i} className="flex items-center text-gray-700 group">
                  <div className="w-5 h-5 rounded-full bg-green-50 flex items-center justify-center mr-3 group-hover:bg-green-100 transition-colors">
                    <Check size={14} className="text-green-600" strokeWidth={3} />
                  </div>
                  <span className="text-base">{item}</span>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Floor Plan */}
        <section>
          <h2 className="text-lg font-medium text-gray-900 mb-4">Floor Plan</h2>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <FloorPlanSVG
              unitType={unit.unit_type?.name || '1-BR'}
              sqft={unit.square_feet || 500}
              className="w-full max-w-md mx-auto"
            />
            <FloorPlanLegend />
            <p className="text-center text-sm text-gray-500 mt-4">
              {unit.unit_type?.name} &bull; {unit.square_feet} sq ft &bull; Floor {unit.floor}
            </p>
          </div>
        </section>

        {/* Contact Section */}
        <section className="bg-gray-50 -mx-4 px-4 py-6 mt-4 rounded-t-2xl">
          <p className="text-base font-medium text-gray-900 mb-4">
            Interested in this unit?
          </p>

          <div className="flex items-center mb-4 bg-white p-3 rounded-xl border border-gray-100 shadow-sm">
            <div className="w-12 h-12 rounded-full bg-primary/10 overflow-hidden mr-3 flex items-center justify-center">
              <span className="text-lg font-medium text-primary">J</span>
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-900">Jennifer M.</p>
              <p className="text-xs text-gray-500">Leasing Agent</p>
            </div>
          </div>

          <div className="flex gap-3">
            {building.leasing_phone && (
              <button
                onClick={handlePhoneClick}
                className="flex-1 h-12 border border-primary text-primary font-medium rounded-lg flex items-center justify-center active:bg-blue-50 transition-colors"
              >
                <Phone size={18} className="mr-2" />
                Call
              </button>
            )}
            <button
              onClick={handleEmailClick}
              className="flex-1 h-12 bg-primary text-white font-medium rounded-lg flex items-center justify-center active:bg-primary-hover shadow-sm transition-all active:scale-[0.98]"
            >
              <Mail size={18} className="mr-2" />
              Email
            </button>
          </div>
        </section>
      </div>

      {/* Saved Units Drawer */}
      <SavedUnitsDrawer
        isOpen={isSavedDrawerOpen}
        onClose={() => setIsSavedDrawerOpen(false)}
      />

      {/* Application Modal */}
      <QuickApplicationModal
        isOpen={isApplicationOpen}
        onClose={() => setIsApplicationOpen(false)}
        building={{
          id: building.id,
          name: building.name,
          address: building.address,
          email: building.leasing_email
        }}
        unit={{
          type: unit.unit_type?.name || 'Unit',
          price: unit.price,
          floor: unit.floor
        }}
      />

      {/* Beaux Bot */}
      <BeauxBot />

      {/* Sticky Footer */}
      <StickyFooter
        buildingName={building.name}
        buildingEmail={building.leasing_email}
        onOpenSaved={() => setIsSavedDrawerOpen(true)}
        onApplyClick={() => setIsApplicationOpen(true)}
      />
    </PublicLayout>
  );
}
